export const EVIDENCE_PREVIEW_BYTE_LIMIT = 1024 * 1024;
export type EvidenceText = { content: string; truncated: boolean };

/** Limit what is read, not just what is rendered: shared source archives can be very large. */
export async function readEvidenceText({
  url,
  mediaType,
  signal,
}: {
  url: string;
  mediaType: string;
  signal: AbortSignal;
}): Promise<EvidenceText> {
  const response = await fetch(url, { credentials: "omit", signal });
  if (!response.ok) {
    throw new Error("Could not load evidence. Please try again.");
  }
  if (!response.body) {
    throw new Error(
      "This browser could not preview the file. You can download the original.",
    );
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let content = "";
  let bytesRead = 0;
  let truncated = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      signal.throwIfAborted();
      if (done) {
        break;
      }
      const remaining = EVIDENCE_PREVIEW_BYTE_LIMIT - bytesRead;
      content += decoder.decode(value.subarray(0, remaining), { stream: true });
      bytesRead += value.byteLength;
      if (bytesRead > EVIDENCE_PREVIEW_BYTE_LIMIT) {
        truncated = true;
        await reader.cancel();
        break;
      }
    }
    content += decoder.decode();
  } finally {
    reader.releaseLock();
  }
  if (
    !truncated &&
    mediaType.split(";")[0].trim().toLowerCase().endsWith("json")
  ) {
    try {
      content = JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      /* Preserve malformed source data as plain text. */
    }
  }
  return { content, truncated };
}

export function evidencePreviewFormat(mediaType: string) {
  const type = mediaType.split(";")[0].trim().toLowerCase();
  if (type === "application/pdf") {
    return "pdf";
  }
  if (type.startsWith("image/") && type !== "image/svg+xml") {
    return "image";
  }
  if (
    type.startsWith("text/") ||
    type.endsWith("json") ||
    type.endsWith("xml")
  ) {
    return "text";
  }
  return "unsupported";
}
